import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Sparkles, BookOpen, Cpu } from "lucide-react";

const HERO_IMG =
  "https://images.pexels.com/photos/1029622/pexels-photo-1029622.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1800";

const SKILLARI_IMG =
  "https://images.unsplash.com/photo-1774360502057-a934d7d0ce60?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

const OMNIA_IMG =
  "https://images.unsplash.com/photo-1764553230197-ca14bbc82b8c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

export default function Home() {
  return (
    <div data-testid="page-home" className="ihm-page-enter">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 ihm-grid-bg pointer-events-none opacity-70" />
        <div
          className="absolute inset-x-0 top-0 h-[640px] bg-cover bg-center opacity-[0.18]"
          style={{ backgroundImage: `url(${HERO_IMG})` }}
        />
        <div className="absolute inset-x-0 top-[520px] h-32 bg-gradient-to-b from-transparent to-white" />

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-24 md:pt-32 pb-32">
          <div className="max-w-4xl">
            <span className="ihm-chip ihm-rise" data-testid="hero-tag">
              <span className="h-1.5 w-1.5 rounded-full bg-[#2997FF] inline-block" />
              Est. 2026 — Lucknow, India
            </span>

            <h1
              data-testid="hero-headline"
              className="font-heading font-bold text-[44px] leading-[1.02] sm:text-6xl md:text-7xl lg:text-[88px] tracking-tighter mt-8 ihm-rise ihm-rise-delay-1"
            >
              Where{" "}
              <span className="relative inline-block">
                <span className="relative z-10">Vision</span>
                <span className="absolute inset-x-0 bottom-1 h-[10px] bg-[#2997FF]/30 -z-0 rounded-sm" />
              </span>{" "}
              Meets{" "}
              <span className="text-[#2997FF]">Execution</span>.
            </h1>

            <p
              data-testid="hero-subheading"
              className="mt-8 max-w-2xl text-[18px] md:text-[22px] leading-relaxed text-[#6E6E73] ihm-rise ihm-rise-delay-2"
            >
              House of IHM EdTech is a technology-driven company creating
              scalable platforms in education and advanced technologies.
            </p>

            <div className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-4 ihm-rise ihm-rise-delay-3">
              <Link
                to="/ventures"
                data-testid="hero-cta-primary"
                className="group inline-flex items-center gap-3 rounded-full bg-[#1D1D1F] px-7 py-4 text-[15px] font-medium text-white transition-all duration-500 hover:bg-[#2997FF] hover:scale-[1.02]"
              >
                Explore Our Ventures
                <ArrowRight
                  size={18}
                  className="transition-transform duration-500 group-hover:translate-x-1"
                />
              </Link>
              <Link
                to="/about"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-3 rounded-full border border-[#1D1D1F] px-7 py-4 text-[15px] font-medium text-[#1D1D1F] transition-colors duration-300 hover:bg-[#F5F5F7]"
              >
                About the company
              </Link>
            </div>
          </div>

          {/* Floating stat strip */}
          <div className="mt-24 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-12 max-w-5xl ihm-rise ihm-rise-delay-4">
            {[
              { k: "02", v: "Active ventures" },
              { k: "EdTech", v: "Core focus" },
              { k: "AI · Wearables", v: "Frontiers explored" },
              { k: "2026", v: "Year incorporated" },
            ].map((s, i) => (
              <div key={i} className="border-l border-[#E5E5EA] pl-5">
                <div className="font-heading font-semibold text-[28px] md:text-[34px] tracking-tight text-[#1D1D1F]">
                  {s.k}
                </div>
                <div className="mt-1 text-[13px] text-[#6E6E73]">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT PREVIEW */}
      <section className="border-t border-[#E5E5EA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-4">
              <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#86868B]">
                01 — The Parent Company
              </span>
              <h2 className="mt-5 font-heading font-bold text-4xl md:text-5xl tracking-tight leading-tight">
                A foundation built for what comes next.
              </h2>
            </div>
            <div className="lg:col-span-7 lg:col-start-6">
              <p
                data-testid="home-about-text"
                className="text-[20px] md:text-[24px] leading-relaxed text-[#1D1D1F]"
              >
                <span className="text-[#86868B]">
                  House of IHM is a technology-driven company focused on building
                  scalable platforms in education and innovation.
                </span>{" "}
                We design products that transform how people learn, build, and
                grow with technology.
              </p>

              <Link
                to="/about"
                data-testid="home-about-cta"
                className="mt-10 inline-flex items-center gap-2 text-[14px] font-medium text-[#1D1D1F] ihm-underline"
              >
                Read the full story
                <ArrowUpRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* VENTURES PREVIEW */}
      <section className="bg-[#F5F5F7]" data-testid="home-ventures-preview">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
            <div>
              <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#86868B]">
                02 — Our Ventures
              </span>
              <h2 className="mt-4 font-heading font-bold text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[1.05]">
                Two projects.
                <br />
                <span className="text-[#86868B]">One mission.</span>
              </h2>
            </div>
            <p className="max-w-md text-[16px] text-[#6E6E73]">
              Each venture is designed to scale, solve real-world problems, and
              create long-term impact.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {/* Skillari card */}
            <Link
              to="/ventures"
              data-testid="venture-card-skillari"
              className="ihm-card group p-8 md:p-10 flex flex-col"
            >
              <div className="relative aspect-[16/10] w-full overflow-hidden rounded-2xl bg-[#EBEBEF]">
                <img
                  src={SKILLARI_IMG}
                  alt="Skillari abstract visual"
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute top-4 left-4 ihm-chip bg-white/90">
                  <BookOpen size={12} /> EdTech
                </div>
              </div>
              <div className="mt-8">
                <div className="flex items-center justify-between">
                  <h3 className="font-heading font-bold text-[28px] md:text-[34px] tracking-tight">
                    Skillari
                  </h3>
                  <ArrowUpRight
                    size={22}
                    className="text-[#1D1D1F] transition-transform duration-500 group-hover:rotate-45"
                  />
                </div>
                <p className="mt-4 text-[15px] leading-relaxed text-[#6E6E73]">
                  A two-sided EdTech marketplace and infrastructure platform
                  enabling tuition teachers and students to connect, learn, and
                  grow.
                </p>
              </div>
            </Link>

            {/* Omnia card */}
            <Link
              to="/ventures"
              data-testid="venture-card-omnia"
              className="ihm-card group p-8 md:p-10 flex flex-col"
            >
              <div className="relative aspect-[16/10] w-full overflow-hidden rounded-2xl bg-[#1D1D1F]">
                <img
                  src={OMNIA_IMG}
                  alt="Omnia metallic visual"
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute top-4 left-4 ihm-chip bg-white/90">
                  <Cpu size={12} /> Wearables
                </div>
              </div>
              <div className="mt-8">
                <div className="flex items-center justify-between">
                  <h3 className="font-heading font-bold text-[28px] md:text-[34px] tracking-tight">
                    Omnia
                  </h3>
                  <ArrowUpRight
                    size={22}
                    className="text-[#1D1D1F] transition-transform duration-500 group-hover:rotate-45"
                  />
                </div>
                <p className="mt-4 text-[15px] leading-relaxed text-[#6E6E73]">
                  A next-generation wearable technology initiative focused on
                  intelligent systems and advanced innovation.
                </p>
              </div>
            </Link>
          </div>

          <div className="mt-14 flex justify-center">
            <Link
              to="/ventures"
              data-testid="home-ventures-cta"
              className="group inline-flex items-center gap-3 rounded-full bg-[#1D1D1F] px-7 py-4 text-[15px] font-medium text-white transition-all duration-500 hover:bg-[#2997FF]"
            >
              View Ventures
              <ArrowRight
                size={18}
                className="transition-transform duration-500 group-hover:translate-x-1"
              />
            </Link>
          </div>
        </div>
      </section>

      {/* Closing band */}
      <section>
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="ihm-dark-block p-10 md:p-16 lg:p-20 relative overflow-hidden">
            <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-[#2997FF]/20 blur-3xl pointer-events-none" />
            <div className="relative max-w-3xl">
              <Sparkles size={28} className="text-[#2997FF]" />
              <h3 className="mt-6 font-heading font-bold text-3xl md:text-5xl tracking-tighter leading-[1.05]">
                Empowering individuals.
                <br />
                Enhancing learning.
                <br />
                <span className="text-[#86868B]">
                  Unlocking new possibilities.
                </span>
              </h3>
              <Link
                to="/vision"
                data-testid="home-vision-cta"
                className="mt-10 inline-flex items-center gap-3 rounded-full bg-white px-6 py-3 text-[14px] font-medium text-[#1D1D1F] transition-all duration-500 hover:bg-[#2997FF] hover:text-white"
              >
                Read our vision
                <ArrowUpRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Backed by BHM Sunrise */}
      <section
        data-testid="backed-by-bhm-section"
        className="border-t border-[#E5E5EA]"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-5">
              <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#86868B]">
                03 — Backing
              </span>
              <h2
                data-testid="backed-by-title"
                className="mt-5 font-heading font-bold text-4xl md:text-5xl tracking-tighter leading-[1.05]"
              >
                Backed by{" "}
                <a
                  href="https://bhmsunrise.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="backed-by-bhm-link"
                  className="text-[#2997FF] ihm-underline"
                >
                  BHM Sunrise
                </a>
                .
              </h2>
            </div>

            <div className="lg:col-span-7">
              <p
                data-testid="backed-by-text"
                className="text-[18px] md:text-[22px] leading-relaxed text-[#1D1D1F]"
              >
                House of IHM is part of{" "}
                <a
                  href="https://bhmsunrise.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="backed-by-bhm-inline-link"
                  className="font-medium text-[#1D1D1F] ihm-underline"
                >
                  BHM Sunrise Pvt. Ltd.
                </a>
                , a multi-venture company supporting innovation across education
                and technology.
              </p>

              <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  { k: "BHM Sunrise", v: "Parent group" },
                  { k: "House of IHM", v: "Venture · this brand" },
                  { k: "Skillari · Omnia", v: "Active initiatives" },
                ].map((s, i) => (
                  <div
                    key={i}
                    data-testid={`backed-by-tier-${i}`}
                    className="rounded-2xl border border-[#E5E5EA] bg-[#F5F5F7] px-5 py-5 transition-colors duration-500 hover:bg-white hover:border-[#D8D8DE]"
                  >
                    <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#86868B]">
                      Tier 0{i + 1}
                    </div>
                    <div className="mt-2 font-heading font-semibold text-[18px] tracking-tight text-[#1D1D1F]">
                      {s.k}
                    </div>
                    <div className="mt-1 text-[13px] text-[#6E6E73]">
                      {s.v}
                    </div>
                  </div>
                ))}
              </div>

              <a
                href="https://bhmsunrise.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="visit-parent-company-cta"
                className="group mt-10 inline-flex items-center gap-3 rounded-full border border-[#1D1D1F] px-6 py-3 text-[14px] font-medium text-[#1D1D1F] transition-all duration-500 hover:bg-[#1D1D1F] hover:text-white"
              >
                Visit Parent Company
                <ArrowUpRight
                  size={16}
                  className="transition-transform duration-500 group-hover:rotate-45"
                />
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
